/**
 * Bootstrap: eine Modulwelt für i18n (laut react-i18next-Doku: "needs to be bundled").
 * Statischer Import von app-bootstrap stellt sicher, dass i18n + init + App dieselbe
 * Modulinstanz nutzen; dynamischer Import könnte getrennte Chunks → zweite Instanz → __store null.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { initI18n, App } from "./app-bootstrap";
import { initTakeleadCursorActive } from "@/lib/takelead-cursor-active";
import { initSentry } from "@/lib/sentry";
import { installClientErrorMonitor, reportClientError } from "@/lib/client-error-monitor";
import { toErrorDisplayMessage } from "@/lib/utils";
import { registerSW } from "virtual:pwa-register";

// Nach Deploy: neuen SW beim nächsten natürlichen Navigate aktivieren.
// `immediate: true` würde skipWaiting() parallel zu laufenden Fetches (z. B. Supabase Auth) auslösen
// und kann in-flight Requests als "Failed to fetch" abbrechen — deshalb aus.
if (import.meta.env.PROD) {
  registerSW({
    onNeedRefresh() {
      /* Waiting SW existiert — nächstes Hard-Reload aktiviert ihn. Kein Auto-Reload. */
    },
  });
}

// Sentry first — catches all subsequent errors including React bootstrap
initSentry();
// Klick-Cursor sofort aktivieren (unabhängig von React-Mount, verhindert Button-Flash)
initTakeleadCursorActive();
installClientErrorMonitor();

function getRootElement(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.getElementById("app");
}

function LoadingFallback() {
  return (
    <div
      style={{
        padding: 24,
        color: "var(--color-text-muted, #64748b)",
        fontFamily: "system-ui, sans-serif",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      Wird geladen…
    </div>
  );
}

function ErrorFallback({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: 24,
        color: "var(--color-danger, #ef4444)",
        fontFamily: "system-ui, sans-serif",
        minHeight: "100vh",
        background: "var(--color-bg, #080c12)",
      }}
    >
      <h1 style={{ fontSize: "1.25rem" }}>Fehler beim Laden</h1>
      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", marginTop: 16 }}>
        {message}
      </pre>
    </div>
  );
}

function runBootstrap(): void {
  const rootEl = getRootElement();
  if (rootEl == null) {
    throw new Error("#app not found");
  }
  const root = createRoot(rootEl);
  root.render(
    <StrictMode>
      <LoadingFallback />
    </StrictMode>,
  );

  initI18n()
    .then(() => {
      root.render(
        <StrictMode>
          <App />
        </StrictMode>,
      );
    })
    .catch((err: unknown) => {
      reportClientError("bootstrap", err);
      const stack = err instanceof Error ? err.stack ?? "" : "";
      const message = toErrorDisplayMessage(err) + (stack ? "\n\n" + stack : "");
      root.render(
        <StrictMode>
          <ErrorFallback message={message} />
        </StrictMode>,
      );
    });
}

if (typeof document !== "undefined" && document.readyState !== "loading") {
  runBootstrap();
} else if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", runBootstrap);
} else {
  runBootstrap();
}
